<script>
import BlockingContainer from '@components/BlockingContainer';
import EntityMultiselect from '@components/EntityMultiselect';
import PointOfInterestModel from '@models/PointOfInterest';
import LocationModel from '@models/Location';

export default {
  components: {
    BlockingContainer,
    EntityMultiselect,
  },
  props: {
    // eslint-disable-next-line
    loadedId: {
      default: null,
    },
  },
  data() {
    return {
      pointOfInterest: {
        name: null,
        location_id: null,
        google_maps_link: null,
        remark: null,
        file: null,
      },
      initialLocationValue: null,
      fileLabel: null,
      removeFile: false,
    };
  },
  computed: {
    LocationModel() {
      return LocationModel;
    },
  },
  mounted() {
    if (this.loadedId) {
      this.$wait.start('point of interest processing');
      PointOfInterestModel.find(this.loadedId)
        .then((response) => {
          this.pointOfInterest = {
            id: response.id,
            name: response.name,
            location_id: response.location_id,
            google_maps_link: response.google_maps_link,
            remark: response.remark,
            file: null,
          };
          this.fileLabel = response.file_name;
          if (response.location) {
            this.initialLocationValue = response.location;
          }
          this.$wait.end('point of interest processing');
        })
        .catch(() => this.$wait.end('point of interest processing'));
    }
  },
  methods: {
    onFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.fileLabel = file.name;
        this.removeFile = false;
      }
    },
    clearFile() {
      this.pointOfInterest.file = null;
      this.fileLabel = null;
      this.removeFile = true;
    },
    validate() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.$wait.start('point of interest processing');
          this.submit();
        }
      });
    },
    submit() {
      const data = Object.assign({}, this.pointOfInterest);
      if (this.loadedId && !data.file) {
        delete data.file;
      }
      if (this.removeFile) {
        data.remove_file = 1;
      }
      new PointOfInterestModel(data)
        .save()
        .then(() => {
          this.$wait.end('point of interest processing');
          this.$emit('submitted');
        })
        .catch(() => this.$wait.end('point of interest processing'));
    },
  },
};
</script>

<template>
  <div>
    <BlockingContainer v-if="$wait.is('point of interest processing')" />
    <b-form @submit.prevent="validate">
      <!-- Name -->
      <b-form-group :label="`${$t('fields.name')}:`">
        <b-form-input
          v-model="pointOfInterest.name"
          v-validate="'required|max:255'"
          data-vv-name="name"
          :data-vv-as="$t('fields.name')"
          type="text"
          :state="errors.has('name') ? false : null"
          :placeholder="$t('placeholders.name')"
        />
        <b-form-invalid-feedback>{{ errors.first('name') }}</b-form-invalid-feedback>
      </b-form-group>

      <!-- File attachment -->
      <b-form-group :label="`${$t('fields.attachment')}:`">
        <b-form-file
          v-model="pointOfInterest.file"
          v-validate="loadedId ? '' : 'required'"
          data-vv-name="file"
          :data-vv-as="$t('fields.attachment')"
          accept=".pdf,.jpg,.jpeg,.png,.mp4"
          :placeholder="fileLabel || $t('placeholders.select_file_with_mp4')"
          :state="errors.has('file') ? false : null"
          @change="onFileChange"
        />
        <b-form-text v-if="fileLabel && !pointOfInterest.file" class="d-flex align-items-center">
          {{ $t('infos.current_file') }}: {{ fileLabel }}
          <b-link class="ml-2 text-danger" @click="clearFile">&times;</b-link>
        </b-form-text>
        <b-form-invalid-feedback>{{ errors.first('file') }}</b-form-invalid-feedback>
      </b-form-group>

      <!-- Location -->
      <b-form-group :label="`${$t('fields.location')}:`">
        <EntityMultiselect
          v-model="pointOfInterest.location_id"
          :model="LocationModel"
          :initial-value="initialLocationValue"
          sort="name"
          :select="['id', 'name']"
          :placeholder="$t('placeholders.location')"
        />
      </b-form-group>

      <!-- Google Maps -->
      <b-form-group :label="`${$t('fields.google_maps')}:`">
        <b-form-input
          v-model="pointOfInterest.google_maps_link"
          type="text"
          :placeholder="$t('placeholders.google_maps')"
        />
      </b-form-group>

      <b-form-group :label="`${$t('fields.remark')}:`">
        <b-form-input
          v-model="pointOfInterest.remark"
          type="text"
          :placeholder="$t('placeholders.remark')"
        />
      </b-form-group>

      <b-button
        :disabled="!!errors.items.length"
        type="submit"
        :variant="'outline-' + (!errors.items.length ? 'dark' : 'danger')"
      >
        {{ $t('actions.submit') }}
      </b-button>
    </b-form>
  </div>
</template>
